import React, { useMemo, useState } from "react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { DatePicker, Input, Select, Tooltip } from "antd";
import dayjs from "dayjs";
import GridSettings from "@controls/grid-settings";
import ReportButton from "@controls/report-button";
import { REPORT_VID } from "@components/service/reports/report-types";
import downloadSrc from "./assets/download.svg";
import plusSrc from "./assets/plus.svg";
import searchSrc from "./assets/search.svg";
import {
  PERIOD_TYPE,
  PERIOD_TYPE_OPTIONS,
  STAY_STATUS,
  STAY_STATUS_OPTIONS
} from "./health-clients-constants";

const toDayjs = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const FILTER_KIND = {
  STATUS: "status",
  DOCTOR: "doctor"
};

const KIND_LABEL = {
  [FILTER_KIND.STATUS]: "Статус",
  [FILTER_KIND.DOCTOR]: "Врач"
};

const HealthClientsFilters = ({
  value,
  doctors,
  loading,
  onChange,
  onRefresh,
  onCreate,
  onImport,
  importAvailable,
  onPeriodTypeChange,
  reportId,
  gridColumns,
  gridStorageKey,
  gridSettings,
  onGridSettingsChange
}) => {
  const byExecutor = value.periodType === PERIOD_TYPE.EXECUTOR_DAY;

  const doctorOptions = useMemo(
    () =>
      (doctors || []).map((d) => ({
        value: d.idSotr ?? d.IdSotr,
        label: d.name ?? d.Name ?? ""
      })),
    [doctors]
  );

  const statusLabel = value.onlyInComplex
    ? STAY_STATUS_OPTIONS.find((o) => o.value === STAY_STATUS.IN)?.label
    : STAY_STATUS_OPTIONS.find((o) => o.value === STAY_STATUS.ALL)?.label;
  const hasStatus = value.onlyInComplex;

  const doctorLabel = doctorOptions.find((o) => o.value === (value.idSotr ?? 0))?.label;
  const hasDoctor = (value.idSotr ?? 0) !== 0;

  const chips = [];
  if (hasStatus) {
    chips.push({ key: FILTER_KIND.STATUS, label: KIND_LABEL[FILTER_KIND.STATUS], value: statusLabel });
  }
  if (hasDoctor) {
    chips.push({ key: FILTER_KIND.DOCTOR, label: KIND_LABEL[FILTER_KIND.DOCTOR], value: doctorLabel });
  }

  const used = new Set(chips.map((c) => c.key));
  const availableKinds = [
    !byExecutor ? { value: FILTER_KIND.STATUS, label: KIND_LABEL[FILTER_KIND.STATUS] } : null,
    { value: FILTER_KIND.DOCTOR, label: KIND_LABEL[FILTER_KIND.DOCTOR] }
  ].filter((k) => k && !used.has(k.value));
  const canAdd = availableKinds.length > 0;

  const [adding, setAdding] = useState(false);
  const [draftKind, setDraftKind] = useState(null);
  const [editKind, setEditKind] = useState(null);

  const patch = (next) => onChange?.({ ...value, ...next });

  const clearKind = (kind) => {
    if (kind === FILTER_KIND.STATUS) {
      patch({ onlyInComplex: false });
    } else if (kind === FILTER_KIND.DOCTOR) {
      patch({ idSotr: 0 });
    }
    if (editKind === kind) setEditKind(null);
  };

  const startAdd = () => {
    setEditKind(null);
    setDraftKind(null);
    setAdding(true);
  };

  const cancelAdd = () => {
    setAdding(false);
    setDraftKind(null);
    setEditKind(null);
  };

  const applyKind = (kind, next) => {
    if (kind === FILTER_KIND.STATUS) {
      patch({ onlyInComplex: next === STAY_STATUS.IN });
    } else if (kind === FILTER_KIND.DOCTOR) {
      patch({ idSotr: next || 0 });
    }
  };

  const pickDraftKind = (kind) => {
    if (kind === FILTER_KIND.STATUS) {
      patch({ onlyInComplex: true });
      cancelAdd();
      return;
    }
    setDraftKind(kind);
  };

  const activeKind = editKind || draftKind;
  const valueOptions =
    activeKind === FILTER_KIND.STATUS
      ? STAY_STATUS_OPTIONS
      : activeKind === FILTER_KIND.DOCTOR
        ? doctorOptions
        : [];

  const pickDraftValue = (next) => {
    if (editKind) {
      if (editKind === FILTER_KIND.DOCTOR && !next) {
        clearKind(editKind);
      } else if (editKind === FILTER_KIND.STATUS && next === STAY_STATUS.ALL) {
        clearKind(editKind);
      } else {
        applyKind(editKind, next);
      }
      setEditKind(null);
      return;
    }
    if (draftKind === FILTER_KIND.DOCTOR && !next) {
      cancelAdd();
      return;
    }
    applyKind(draftKind, next);
    cancelAdd();
  };

  return (
    <>
      <div className="health-clients__filters">
        <div className="health-clients__filters-actions" role="group" aria-label="Действия">
          <Tooltip title="Регистрация пациента">
            <button
              type="button"
              className="health-clients__add-icon-btn"
              aria-label="Регистрация пациента"
              onClick={onCreate}
            >
              <span className="health-clients__add-icon-btn-icon">
                <img src={plusSrc} alt="" />
              </span>
            </button>
          </Tooltip>
          {importAvailable ? (
            <Tooltip title="Импорт">
              <button
                type="button"
                className="health-clients__import-icon-btn"
                aria-label="Импорт"
                onClick={onImport}
              >
                <span className="health-clients__import-icon-btn-icon">
                  <img src={downloadSrc} alt="" />
                </span>
              </button>
            </Tooltip>
          ) : null}
          <Tooltip title="Печать">
            <span>
              <ReportButton
                vid={REPORT_VID.CLIENTS}
                id={reportId}
                begPer={value.dateBeg}
                endPer={value.dateEnd}
                disabled={reportId == null}
                compact
              />
            </span>
          </Tooltip>
          <Tooltip title="Настройка таблицы">
            <span>
              <GridSettings
                title="Настройка таблицы"
                storageKey={gridStorageKey}
                columns={gridColumns}
                value={gridSettings}
                onChange={onGridSettingsChange}
              />
            </span>
          </Tooltip>
        </div>

        <div className="health-clients__field">
          <Select
            value={value.periodType}
            options={PERIOD_TYPE_OPTIONS}
            popupMatchSelectWidth={false}
            suffixIcon={null}
            title="Период"
            aria-label="Период"
            onChange={(periodType) => onPeriodTypeChange(periodType)}
          />
        </div>

        {byExecutor ? (
          <div className="health-clients__field">
            <DatePicker
              allowClear
              format="DD.MM.YYYY"
              suffixIcon={null}
              title="Дата"
              aria-label="Дата"
              value={toDayjs(value.dateBeg)}
              onChange={(d) => patch({ dateBeg: d ? d.format("YYYY-MM-DD") : null })}
            />
          </div>
        ) : (
          <>
            <div className="health-clients__field">
              <DatePicker
                allowClear
                format="DD.MM.YYYY"
                suffixIcon={null}
                title="Дата с"
                aria-label="Дата с"
                value={toDayjs(value.dateBeg)}
                onChange={(d) => patch({ dateBeg: d ? d.format("YYYY-MM-DD") : null })}
              />
            </div>
            <div className="health-clients__field">
              <DatePicker
                allowClear
                format="DD.MM.YYYY"
                suffixIcon={null}
                title="Дата по"
                aria-label="Дата по"
                value={toDayjs(value.dateEnd)}
                onChange={(d) => patch({ dateEnd: d ? d.format("YYYY-MM-DD") : null })}
              />
            </div>
          </>
        )}

        <div className="health-clients__search" role="group" aria-label="Поиск и фильтры">
          <span className="health-clients__search-icon">
            <img src={searchSrc} alt="" />
          </span>
          <Input
            variant="borderless"
            allowClear
            className="health-clients__search-input"
            value={value.name ?? ""}
            placeholder="Поиск ФИО / Номер карты"
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            onPressEnter={onRefresh}
          />

          {chips.map((chip) =>
            editKind === chip.key ? (
              <span key={chip.key} className="health-clients__search-draft">
                <Select
                  autoFocus
                  defaultOpen
                  showSearch={chip.key === FILTER_KIND.DOCTOR}
                  optionFilterProp="label"
                  variant="borderless"
                  suffixIcon={null}
                  popupMatchSelectWidth={false}
                  style={{ minWidth: 120 }}
                  value={
                    chip.key === FILTER_KIND.STATUS
                      ? (value.onlyInComplex ? STAY_STATUS.IN : STAY_STATUS.ALL)
                      : value.idSotr
                  }
                  options={valueOptions}
                  onChange={pickDraftValue}
                />
                <button
                  type="button"
                  className="health-clients__search-chip-clear"
                  title="Закрыть"
                  aria-label="Закрыть"
                  onClick={() => setEditKind(null)}
                >
                  <CloseOutlined />
                </button>
              </span>
            ) : (
              <span key={chip.key} className="health-clients__search-chip">
                <button
                  type="button"
                  className="health-clients__search-chip-btn"
                  title="Изменить значение"
                  onClick={() => {
                    setAdding(false);
                    setDraftKind(null);
                    setEditKind(chip.key);
                  }}
                >
                  <span className="health-clients__search-chip-kind">{chip.label}</span>
                  <span className="health-clients__search-chip-value">{chip.value}</span>
                </button>
                <button
                  type="button"
                  className="health-clients__search-chip-clear"
                  title="Сбросить"
                  aria-label={`Сбросить: ${chip.label}`}
                  onClick={() => clearKind(chip.key)}
                >
                  <CloseOutlined />
                </button>
              </span>
            )
          )}

          {adding ? (
            <span className="health-clients__search-draft">
              {draftKind == null ? (
                <Select
                  autoFocus
                  defaultOpen
                  variant="borderless"
                  suffixIcon={null}
                  popupMatchSelectWidth={false}
                  style={{ minWidth: 110 }}
                  placeholder="Фильтр"
                  value={draftKind}
                  options={availableKinds}
                  onChange={pickDraftKind}
                />
              ) : (
                <Select
                  autoFocus
                  defaultOpen
                  showSearch={draftKind === FILTER_KIND.DOCTOR}
                  optionFilterProp="label"
                  variant="borderless"
                  suffixIcon={null}
                  popupMatchSelectWidth={false}
                  style={{ minWidth: 120 }}
                  placeholder="Значение"
                  options={valueOptions}
                  onChange={pickDraftValue}
                />
              )}
              <button
                type="button"
                className="health-clients__search-chip-clear"
                title="Отменить"
                aria-label="Отменить добавление фильтра"
                onClick={cancelAdd}
              >
                <CloseOutlined />
              </button>
            </span>
          ) : null}

          {canAdd && !adding && !editKind ? (
            <button
              type="button"
              className="health-clients__search-add"
              title="Добавить фильтр"
              aria-label="Добавить фильтр"
              onClick={startAdd}
            >
              <PlusOutlined />
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default HealthClientsFilters;
